import type { Coordinate } from '../models/editor-feature';
import { straightenFrom } from './drawing';

/**
 * Lining things up with each other while they are moved, reshaped or drawn.
 *
 * Unlike snapping to a grid or onto a corner, this only ever nudges along one axis at a time
 * and only onto something that is already on the plan: when a room's edge comes within a few
 * pixels of the same height as another room's edge, it settles exactly level with it, and a
 * guide line shows what it lined up with. It is how two rooms end up flush, a row of desks
 * ends up straight and a wall ends up square, without anybody measuring anything.
 */

/** A line to draw while something is aligned: across at `value` (y) or up and down at `value` (x). */
export interface AlignmentGuide {
  readonly axis: 'x' | 'y';
  /** Where the line is: an x for a vertical line, a y for a horizontal one. */
  readonly value: number;
  /** How far it runs along the other axis, so it joins the two things it lines up. */
  readonly from: number;
  readonly to: number;
}

export interface AlignmentResult {
  /** The correction to add to the move, in plan units. Zero on an axis nothing lined up on. */
  readonly dx: number;
  readonly dy: number;
  readonly guides: AlignmentGuide[];
}

export const NO_ALIGNMENT: AlignmentResult = { dx: 0, dy: 0, guides: [] };

/** How close two values have to be, in plan units, to count as the same line once aligned. */
const SAME_LINE = 1e-9;

/**
 * The correction that lines `moving` up with `targets`, on each axis independently.
 *
 * Every moving point is tried against every target, and the smallest correction within
 * `tolerance` wins per axis — so a room being dragged lines up by whichever of its corners is
 * nearest to lining up, not by one chosen in advance.
 *
 * @param lockX / lockY An axis that must not be corrected: the one held still while moving
 * straight across or straight up.
 */
export const alignPoints = (
  moving: readonly Coordinate[],
  targets: readonly Coordinate[],
  tolerance: number,
  options: { readonly lockX?: boolean; readonly lockY?: boolean } = {},
): AlignmentResult => {
  if (!moving.length || !targets.length || !(tolerance > 0)) {
    return NO_ALIGNMENT;
  }

  let bestX: { correction: number; value: number } | undefined;
  let bestY: { correction: number; value: number } | undefined;

  moving.forEach(([mx, my]) => {
    targets.forEach(([tx, ty]) => {
      const cx = tx - mx;
      const cy = ty - my;

      if (!options.lockX && Math.abs(cx) <= tolerance && (!bestX || Math.abs(cx) < Math.abs(bestX.correction))) {
        bestX = { correction: cx, value: tx };
      }

      if (!options.lockY && Math.abs(cy) <= tolerance && (!bestY || Math.abs(cy) < Math.abs(bestY.correction))) {
        bestY = { correction: cy, value: ty };
      }
    });
  });

  const dx = bestX?.correction ?? 0;
  const dy = bestY?.correction ?? 0;
  const placed = moving.map(([x, y]): Coordinate => [x + dx, y + dy]);
  const guides: AlignmentGuide[] = [];

  // Each guide spans everything that now sits on it, moving and still alike, so the line reads
  // as "these are level" rather than pointing at a single corner.
  if (bestX) {
    const x = bestX.value;
    const along = [...placed, ...targets]
      .filter(([px]) => Math.abs(px - x) <= SAME_LINE + Math.abs(x) * SAME_LINE)
      .map(([, py]) => py);
    guides.push({ axis: 'x', value: x, from: Math.min(...along), to: Math.max(...along) });
  }

  if (bestY) {
    const y = bestY.value;
    const along = [...placed, ...targets]
      .filter(([, py]) => Math.abs(py - y) <= SAME_LINE + Math.abs(y) * SAME_LINE)
      .map(([px]) => px);
    guides.push({ axis: 'y', value: y, from: Math.min(...along), to: Math.max(...along) });
  }

  return { dx, dy, guides };
};

/**
 * The corners and centre of the box around some points: what a moved shape lines up by.
 *
 * The box rather than every vertex, because lining up is about edges and middles — a room is
 * flush with another by its side, and centred under it by its middle — and because a traced
 * outline can have hundreds of vertices that would each pull on the move.
 */
export const alignmentHandlesOf = (points: readonly Coordinate[]): Coordinate[] => {
  if (!points.length) {
    return [];
  }

  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return [[minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY], [(minX + maxX) / 2, (minY + maxY) / 2]];
};

/** A move held to one axis: whichever the pointer has travelled further along. */
export const lockToAxis = (dx: number, dy: number): { dx: number; dy: number; lockX: boolean; lockY: boolean } => (
  Math.abs(dx) >= Math.abs(dy)
    ? { dx, dy: 0, lockX: false, lockY: true }
    : { dx: 0, dy, lockX: true, lockY: false }
);

/**
 * How close, as a share of the shorter side beside it, a corner has to be to a full right angle
 * for the right angle to win over straightening just one side.
 */
const RIGHT_ANGLE_REACH = 0.15;

/**
 * A corner put square with the corners either side of it.
 *
 * The right angle both sides meet at wins when the corner is anywhere near it — that is what
 * turns a hand-dragged room corner back into a rectangle's, and what somebody holding Alt on a
 * corner is after. Further off, only the nearer side is straightened: level or upright with the
 * previous corner or with the next, whichever is closer to where the pointer is.
 */
export const squareCornerAt = (point: Coordinate, previous: Coordinate, next: Coordinate): Coordinate => {
  const distance = (candidate: Coordinate): number => Math.hypot(candidate[0] - point[0], candidate[1] - point[1]);
  const nearest = (candidates: Coordinate[]): Coordinate => candidates.reduce(
    (best, candidate) => (distance(candidate) < distance(best) ? candidate : best),
  );

  const rightAngle = nearest([[previous[0], next[1]], [next[0], previous[1]]]);
  const shorterSide = Math.min(
    Math.hypot(point[0] - previous[0], point[1] - previous[1]),
    Math.hypot(point[0] - next[0], point[1] - next[1]),
  );

  if (distance(rightAngle) <= shorterSide * RIGHT_ANGLE_REACH) {
    return rightAngle;
  }

  return nearest([straightenFrom(previous, point), straightenFrom(next, point)]);
};
